import { t } from '@avalara/bat-apps-shared/i18n';
import { SAlert, SCardAction, SCol, SIcon, SRow } from '@avalara/skylab-sdk/react';

import { CheckItem } from '../../shared/accordion-styled-components';
import '../product-catalog.css';
import { usePricingCtaClick } from './use-pricing-cta';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { ProductDetailSection, ProductPricingTier } from '../../../../../types/product-detail';

type Props = {
  pricingTiers: ProductDetailSection<ProductPricingTier>;
  onContactClick: () => void;
};

// Splits an originalPrice like "$291 Activation Fee | Waived" into the struck-through price/fee
// part and the plain suffix describing the waiver — the suffix isn't crossed out since it
// describes current, active state rather than something no longer in effect.
const splitOriginalPrice = (originalPrice: string) => {
  const [struckPrice, suffix] = originalPrice.split(' | ');
  return { struckPrice, suffix };
};

const ProductDetailPricingTiers = ({ pricingTiers: { heading, description, items }, onContactClick }: Props) => {
  const handleCtaClick = usePricingCtaClick(onContactClick);

  if (!items.length) return null;

  // The disclaimer talks specifically about purchase renewal/standard pricing — only relevant when
  // this section actually offers something to buy. A tier list made up of only 'trial'/'contact'
  // tiers has nothing for it to describe.
  const hasBuyTier = items.some((tier) => tier.ctaAction === 'buy');

  return (
    <div id='buy-product-detail-pricing' data-ref='buy.product-detail.pricing-tiers-section'>
      <h2 className='product-detail-section-heading' data-ref='buy.product-detail.pricing.heading'>
        {heading ? t(heading as TranslationKey) : t('client.product-detail.pricing-heading')}
      </h2>
      {description && (
        <p className='pad-bottom-md' data-ref='buy.product-detail.pricing.subheading'>
          {t(description as TranslationKey)}
        </p>
      )}
      {hasBuyTier && (
        <SAlert
          status='info'
          noDismiss
          class='product-detail-pricing-disclaimer show-icon margin-top-none'
          data-ref='buy.product-detail.pricing.disclaimer'
        >
          {t('client.product-detail.pricing-disclaimer')}
        </SAlert>
      )}
      <SRow data-ref='buy.product-detail.pricing-tiers'>
        {items.map((tier) => {
          const originalPrice = tier.originalPrice ? splitOriginalPrice(tier.originalPrice) : undefined;
          // A 'buy' tier can reach the client with no offerFlow (e.g. ecm-pro.ts/avalara-tax-research.ts
          // fall back to `offerFlow: undefined` when the live offer lookup doesn't resolve one) —
          // handleCtaClick silently no-ops in that case, so disable the CTA rather than leave the
          // customer clicking a live-looking button with no feedback.
          const isCtaDisabled = tier.ctaAction === 'buy' && !tier.offerFlow;

          return (
            <SCol
              className={`pad-all-none${tier.offset ? ' pad-left-md' : ''}`}
              key={tier.id}
              span='auto'
              data-ref={`buy.product-detail.pricing-tier.${tier.id}`}
            >
              <SCardAction
                className={`product-detail-pricing-tier${tier.highlighted ? ' product-detail-pricing-tier--highlighted' : ''}`}
              >
                {tier.featuredLabel && (
                  <span slot='status' className='product-detail-pricing-tier-featured-label'>
                    {tier.featuredLabel}
                  </span>
                )}
                <h3 slot='title'>{tier.name}</h3>
                {tier.subtitle && (
                  <h3 className='margin-top-md' slot='subtitle'>
                    {tier.subtitle}
                  </h3>
                )}
                <div slot='description'>
                  {tier.bodyText && (
                    <h3 className='product-detail-pricing-tier-body-text'>
                      <span className='font-bold'>{tier.bodyText}</span>
                    </h3>
                  )}
                  {tier.price && (
                    <p data-ref={`buy.product-detail.pricing-tier.${tier.id}.price`}>
                      <span className='text-xxl font-bold'>{tier.price}</span>
                      {tier.priceSubtext && <span>{tier.priceSubtext}</span>}
                    </p>
                  )}
                  {originalPrice && (
                    <span className='product-detail-pricing-tier-original-price'>
                      <span className='product-detail-pricing-tier-original-price-struck'>
                        {originalPrice.struckPrice}
                      </span>
                      {originalPrice.suffix && ` | ${originalPrice.suffix}`}
                    </span>
                  )}
                  {tier.badge && (
                    <span
                      className='product-detail-pricing-tier-badge'
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.badge`}
                    >
                      {tier.badge}
                    </span>
                  )}
                  {tier.bullets?.map((bullet) => (
                    <CheckItem key={bullet}>
                      <SIcon className='product-detail-pricing-tier-bullets' name='check-circle' aria-hidden='true' />
                      <span>{bullet}</span>
                    </CheckItem>
                  ))}
                  {tier.priceTable && (
                    <table
                      className='product-detail-pricing-tier-price-table'
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.price-table`}
                    >
                      <thead>
                        <tr>
                          <th>{t('client.product-detail.price-table.range-header')}</th>
                          <th>{t('client.product-detail.price-table.price-header')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tier.priceTable.map((row) => (
                          <tr key={row.range}>
                            <td>{row.range}</td>
                            <td>{row.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div slot='footer' className='flex flex-dir-col full-width'>
                  <button
                    type='button'
                    className={tier.highlighted ? 'primary' : 'secondary'}
                    data-ref={`buy.product-detail.pricing-tier.${tier.id}.cta`}
                    onClick={() => handleCtaClick(tier.id, tier)}
                    disabled={isCtaDisabled}
                  >
                    {tier.ctaText}
                  </button>
                  {tier.finePrint && <p className='text-sm'>{tier.finePrint}</p>}
                </div>
              </SCardAction>
            </SCol>
          );
        })}
      </SRow>
    </div>
  );
};

export default ProductDetailPricingTiers;
